/**
 * Canonical confidence computation.
 *
 * One pure function. Same inputs → same output. No DB, no I/O, no defaults
 * that lie ("50%" for missing data).
 *
 * Formula:
 *
 *   raw =   w_consensus * consensus
 *         + w_coverage  * coverage
 *         + w_recency   * recency
 *         + w_agreement * agreement
 *         - w_dispute   * dispute
 *
 * Then passed through an optional Platt sigmoid (calibrate) that maps raw
 * scores to observed accuracy. Without a calibration row the identity
 * transform is used so the number stays interpretable.
 */

import { resolveBand, DEFAULT_BAND_THRESHOLDS, type BandThresholds } from './bands';
import type {
  ConfidenceContributors,
  ConfidenceInputs,
  ConfidenceReport,
} from './types';

export interface ConfidenceWeights {
  consensus: number;
  coverage: number;
  recency: number;
  agreement: number;
  dispute: number;
}

/**
 * Weights sum to 1 across the *positive* contributors. Dispute is a
 * subtractive penalty so it isn't in the sum.
 */
export const DEFAULT_WEIGHTS: ConfidenceWeights = {
  consensus: 0.35,
  coverage: 0.3,
  recency: 0.15,
  agreement: 0.2,
  dispute: 0.25,
};

/** Platt sigmoid: 1 / (1 + exp(-(a*x + b))). Identity when a=1, b=0-ish. */
export interface PlattParams {
  a: number;
  b: number;
}
/**
 * Truly-null calibration marker. When no calibrated `PlattParams` are
 * supplied we skip the sigmoid entirely instead of running the old
 * `{a:6, b:-3}` fake-identity — that curve stretched raw 0.3 to 0.18 and
 * raw 0.7 to 0.82, making mid-range scores look confidently extreme even
 * though nothing had actually been calibrated.
 */
export const IDENTITY_PLATT: PlattParams | null = null;

export interface ComputeOptions {
  weights?: ConfidenceWeights;
  thresholds?: BandThresholds;
  /** Per-framework Platt calibration params from `confidence_calibration`. */
  calibration?: PlattParams | null;
  /** Minimum sources required to emit any value at all. */
  minSources?: number;
}

interface CoverageParts {
  total: number;
  base: number;
  singleSourceBoost: number;
  evidenceBoost: number;
  frameworkBoost: number;
}

function coverageParts(inputs: ConfidenceInputs): CoverageParts | undefined {
  const n = inputs.nSources ?? 0;
  if (n <= 0) return undefined;
  // Saturating: 1 source ≈ 0.35, 2 ≈ 0.6, 3 ≈ 0.78, 5+ ≈ 0.95.
  const base = 1 - Math.pow(0.7, n);
  // Boost for a single high-weight specialized quiz.
  const singleSourceBoost =
    n === 1 ? Math.max(0, (inputs.strongestSourceWeight ?? 0.4) - 0.4) : 0;
  const evidenceCount = Math.max(0, inputs.evidenceCount ?? 0);
  const evidenceBoost =
    evidenceCount > 1 ? Math.min(0.22, Math.log1p(evidenceCount) / 16) : 0;

  const frameworkCoverage = inputs.frameworkCoverage;
  const frameworkBoost =
    typeof frameworkCoverage === 'number' && Number.isFinite(frameworkCoverage)
      ? Math.min(0.18, Math.max(0, frameworkCoverage) * 0.18)
      : 0;

  const total = Math.min(1, base + singleSourceBoost + evidenceBoost + frameworkBoost);
  return { total, base, singleSourceBoost, evidenceBoost, frameworkBoost };
}

function coverageFromInputs(inputs: ConfidenceInputs): number | undefined {
  return coverageParts(inputs)?.total;
}

function recencyFromAge(days: number | null | undefined): number | undefined {
  if (days === null || days === undefined) return undefined;
  // 10%/month decay matches canonical architecture doc.
  // months = days / 30 → factor = 0.9^months
  const months = days / 30;
  return Math.max(0, Math.min(1, Math.pow(0.9, months)));
}

function applyPlatt(x: number, p: PlattParams): number {
  const z = p.a * x + p.b;
  return 1 / (1 + Math.exp(-z));
}

export function computeConfidence(
  inputs: ConfidenceInputs,
  opts: ComputeOptions = {},
): ConfidenceReport {
  const w = opts.weights ?? DEFAULT_WEIGHTS;
  const thresholds = opts.thresholds ?? DEFAULT_BAND_THRESHOLDS;
  const calibration = opts.calibration ?? IDENTITY_PLATT; // null = no sigmoid
  const minSources = opts.minSources ?? 1;

  const nSources = inputs.nSources ?? 0;

  const parts = coverageParts(inputs);
  const contributors: ConfidenceContributors = {
    consensus: inputs.consensusStrength ?? undefined,
    coverage: parts?.total,
    coverageBreakdown: parts
      ? {
          base: parts.base,
          singleSourceBoost: parts.singleSourceBoost,
          evidenceBoost: parts.evidenceBoost,
          frameworkBoost: parts.frameworkBoost,
        }
      : undefined,
    recency: recencyFromAge(inputs.ageDays),
    agreement: inputs.crossFrameworkAgreement ?? undefined,
    dispute: inputs.disputePenalty ?? undefined,
  };

  if (nSources < minSources) {
    return {
      value: null,
      band: 'insufficient',
      nSources,
      lastUpdated: inputs.lastUpdated ?? null,
      contributors,
      reason: 'Not enough signals yet — complete an assessment to see confidence.',
    };
  }

  // Weighted sum over *available* contributors. Missing contributors don't
  // count against you; they just don't help. Divide by the sum of the
  // present weights so a partial input isn't artificially deflated.
  let numerator = 0;
  let denominator = 0;
  const push = (weight: number, value: number | undefined) => {
    if (value === undefined) return;
    numerator += weight * value;
    denominator += weight;
  };
  push(w.consensus, contributors.consensus);
  push(w.coverage, contributors.coverage);
  push(w.recency, contributors.recency);
  push(w.agreement, contributors.agreement);

  if (denominator === 0) {
    return {
      value: null,
      band: 'insufficient',
      nSources,
      lastUpdated: inputs.lastUpdated ?? null,
      contributors,
      reason: 'No calibrated inputs yet — take a scored assessment to see confidence.',
    };
  }

  const positive = numerator / denominator;
  const penalized = Math.max(0, positive - w.dispute * (contributors.dispute ?? 0));
  const calibrated = calibration
    ? applyPlatt(penalized, calibration)
    : penalized;

  const band = resolveBand(calibrated, thresholds);

  return {
    value: Math.max(0, Math.min(1, calibrated)),
    band,
    nSources,
    lastUpdated: inputs.lastUpdated ?? null,
    contributors,
    reason: explain(contributors, band, nSources),
  };
}

function explain(
  c: ConfidenceContributors,
  band: string,
  nSources: number,
): string {
  const parts: string[] = [];
  parts.push(`${nSources} source${nSources === 1 ? '' : 's'}`);
  if (c.consensus !== undefined) parts.push(`consensus ${Math.round(c.consensus * 100)}%`);
  if (c.recency !== undefined && c.recency < 0.6) parts.push('data is aging');
  if (c.dispute !== undefined && c.dispute > 0) parts.push('user contradictions');
  return `${band[0].toUpperCase()}${band.slice(1)} — ${parts.join(' · ')}`;
}
